// Copyright (c) 2025 WiseTime. All rights reserved.

import { Observable, of } from "rxjs"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { create } from "@bufbuild/protobuf"
import type { Empty } from "@bufbuild/protobuf/wkt"
import * as Actions from "../actions"
import * as Slice from "../slice"
import { showSnackBar } from "../../snackbar/actions"
import { Code, executorMutation, Init, isConnectError, timesheetFieldService } from "../../../app/rpc"
import {
  DeleteTimesheetFieldOptionRequestSchema,
} from "../../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"

type DeleteTimesheetFieldRpc = (request: Init<typeof DeleteTimesheetFieldOptionRequestSchema>) => Observable<Empty>

export const deleteTimesheetFieldOption = (deleteTimesheetFieldOptionRpc: DeleteTimesheetFieldRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.deleteTimesheetFieldOption.match),
      map(action => action.payload),
      mergeMap(payload =>
        deleteTimesheetFieldOptionRpc(payload.request).pipe(
          mergeMap(() => {
            // Hide dialog
            if (payload.onSuccess) {
              payload.onSuccess()
            }
            return of(Slice.deleteTimesheetFieldOption({
              timesheetFieldId: payload.timesheetFieldId,
              optionId: payload.request.id ?? "",
            }))
          }),
          catchError(e => handleError(e, payload)),
        )
      ),
    )

const handleError = (e: unknown, payload: Actions.DeleteTimesheetFieldOptionPayload): Observable<Action> => {
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.FailedPrecondition) {
      // Hide dialog
      if (payload.onSuccess) {
        payload.onSuccess()
      }
      return of(showSnackBar({
        message: t({
          id: "msg_delete_timesheet_field_option_failed_precondition",
          message: "This option is in use and cannot be deleted. Please archive instead.",
        }),
        messageType: "ERROR",
      }))
    }
    if (code === Code.NotFound) {
      // Hide dialog
      if (payload.onSuccess) {
        payload.onSuccess()
      }
      return of(Slice.deleteTimesheetFieldOption({
        timesheetFieldId: payload.timesheetFieldId,
        optionId: payload.request.id ?? "",
      }))
    }
    if (code === Code.PermissionDenied) {
      // Reset progress
      if (payload.onError) {
        payload.onError()
      }
      return of(showSnackBar({
        message: t({
          id: "msg_delete_timesheet_field_option_permission_denied",
          message: "Failed to delete timesheet field option. You do not have permission.",
        }),
        messageType: "ERROR",
      }))
    }
  }
  // Reset progress
  if (payload.onError) {
    payload.onError()
  }
  return of(showSnackBar({
    message: t({
      id: "msg_delete_timesheet_field_option_failed",
      message: "Failed to delete timesheet field option. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
    }),
    messageType: "ERROR",
  }))
}

const deleteTimesheetFieldOptionRpc = (request: Init<typeof DeleteTimesheetFieldOptionRequestSchema>): Observable<Empty> =>
  executorMutation(() => timesheetFieldService.deleteTimesheetFieldOption(create(DeleteTimesheetFieldOptionRequestSchema, request)))

export default deleteTimesheetFieldOption(deleteTimesheetFieldOptionRpc)
